
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Stack, Chip, Tooltip, Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const apiUrl = '/api/expenses';

export default function Expenses() {
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [expenses, setExpenses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [sort, setSort] = useState({ field: 'expense_date', direction: 'desc' });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ expense_date: '', amount: '', category: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate total expenses
  const total = useMemo(() => expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0), [expenses]);

  // Default categories for office/shop/mall
  const defaultCategories = [
    'Utilities', 'Rent', 'Salaries', 'Stationery', 'Maintenance', 'Repairs', 'Cleaning', 'Security',
    'Travel', 'Food', 'Marketing', 'Supplies', 'IT', 'Internet', 'Electricity', 'Water', 'Telephone',
    'Transport', 'Legal', 'Consulting', 'Insurance', 'Taxes', 'Miscellaneous', 'Other'
  ];

  // Category color map
  const categoryColors = {
    Utilities: 'warning',
    Rent: 'secondary',
    Salaries: 'error',
    Stationery: 'info',
    Maintenance: 'primary',
    Repairs: 'primary',
    Cleaning: 'success',
    Security: 'info',
    Travel: 'primary',
    Food: 'success',
    Marketing: 'secondary',
    Supplies: 'info',
    IT: 'info',
    Internet: 'info',
    Electricity: 'warning',
    Water: 'info',
    Telephone: 'info',
    Transport: 'primary',
    Legal: 'secondary',
    Consulting: 'secondary',
    Insurance: 'secondary',
    Taxes: 'warning',
    Miscellaneous: 'default',
    Other: 'default',
  };

  const fetchExpenses = async () => {
    const res = await fetch(apiUrl);
    setExpenses(await res.json());
  };


  useEffect(() => { fetchExpenses(); }, []);

  // Filtering and searching
  useEffect(() => {
    let data = [...expenses];
    if (filterCategory) data = data.filter(e => e.category === filterCategory);
    if (filterFrom) data = data.filter(e => e.expense_date >= filterFrom);
    if (filterTo) data = data.filter(e => e.expense_date <= filterTo);
    if (search) {
      const s = search.toLowerCase();
      data = data.filter(e =>
        (e.category && e.category.toLowerCase().includes(s)) ||
        (e.description && e.description.toLowerCase().includes(s)) ||
        (e.amount && String(e.amount).includes(s))
      );
    }
    // Sorting
    data.sort((a, b) => {
      let v1 = a[sort.field], v2 = b[sort.field];
      if (sort.field === 'amount') {
        v1 = Number(v1); v2 = Number(v2);
      }
      if (v1 < v2) return sort.direction === 'asc' ? -1 : 1;
      if (v1 > v2) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFiltered(data);
    setPage(0); // Reset to first page on filter/sort change
  }, [expenses, search, filterCategory, filterFrom, filterTo, sort]);

  const handleOpen = (exp = null) => {
    if (exp) {
      setEditId(exp.id);
      setForm({
        expense_date: exp.expense_date,
        amount: exp.amount,
        category: exp.category,
        description: exp.description || '',
      });
    } else {
      setEditId(null);
      setForm({ expense_date: '', amount: '', category: '', description: '' });
    }
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ expense_date: '', amount: '', category: '', description: '' });
    setError('');
  };

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.expense_date || !form.amount || !form.category) {
      setError('Date, amount, and category are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(editId ? `${apiUrl}/${editId}` : apiUrl, {
        method: editId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save expense');
      await fetchExpenses();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      await fetchExpenses();
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box className="expenses-page" sx={{ flexGrow: 1, bgcolor: 'background.default', p: { xs: 1, md: 3 }, minHeight: '100vh', width: '100%' }}>
      <Stack className="expenses-header" direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>Expenses</Typography>
          <Typography variant="body2" color="text.secondary">Track and manage your business expenses</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ borderRadius: 2, fontWeight: 600, boxShadow: 1 }}>Add Expense</Button>
      </Stack>

      {/* Summary Card */}
      <Paper className="expenses-summary" elevation={3} sx={{ mb: 3, p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', background: 'linear-gradient(135deg,#fb8c00 60%,#ffe082 100%)', color: '#fff' }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 40, mr: 2, opacity: 0.18 }} />
        <Box className="expenses-kpi-copy">
          <Typography variant="subtitle2" sx={{ color: '#fff', opacity: 0.85 }}>Total Expenses</Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff' }}>{total.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}</Typography>
        </Box>
        <Box className="expenses-kpi-meta" sx={{ flex: 1 }} />
        <Chip label={expenses.length + ' records'} color="warning" variant="filled" sx={{ fontWeight: 600, bgcolor: '#fff3e0', color: '#fb8c00', fontSize: 14 }} />
      </Paper>

      {/* Filters and Search - moved below summary, above table */}
      <Paper className="expenses-filters" elevation={0} sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: '#fffde7', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          value={filterFrom}
          onChange={e => setFilterFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filterTo}
          onChange={e => setFilterTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140 }}
        />
        <Autocomplete
          options={['', ...defaultCategories]}
          value={filterCategory}
          onChange={(_, v) => setFilterCategory(v || '')}
          renderInput={params => <TextField {...params} label="Category" size="small" sx={{ minWidth: 160 }} />}
        />
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <Button onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterCategory(''); setSearch(''); }} size="small" color="inherit" variant="outlined">Clear</Button>
      </Paper>

      {/* Expenses Table */}
      <Paper className="expenses-table-shell" elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ background: 'linear-gradient(90deg,#ffe0b2 0,#fffde7 100%)' }}>
              <TableRow>
                {['expense_date', 'amount', 'category', 'description'].map((col, idx) => (
                  <TableCell
                    key={col}
                    sx={{ fontWeight: 700, color: '#fb8c00', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setSort(s => ({
                      field: col,
                      direction: s.field === col ? (s.direction === 'asc' ? 'desc' : 'asc') : 'asc',
                    }))}
                  >
                    {col === 'expense_date' ? 'Date' :
                     col === 'amount' ? 'Amount' :
                     col === 'category' ? 'Category' :
                     col === 'description' ? 'Description' : col}
                    {sort.field === col ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, color: '#fb8c00' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((exp) => (
                <TableRow key={exp.id} hover sx={{ transition: 'background 0.2s', '&:hover': { background: '#fff8e1' } }}>
                  <TableCell>{exp.expense_date}</TableCell>
                  <TableCell>
                    <Chip
                      label={Number(exp.amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 })}
                      color="warning"
                      size="small"
                      icon={<TrendingDownIcon sx={{ fontSize: 18 }} />}
                      sx={{ fontWeight: 600, bgcolor: '#fff3e0', color: '#fb8c00' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={exp.category}
                      color={categoryColors[exp.category] || 'default'}
                      size="small"
                      sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis' }}>{exp.description}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(exp)}><EditIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(exp.id)}><DeleteIcon /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: '#aaa', py: 4 }}>
                    No expenses found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination Controls */}
        {filtered.length > rowsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 2 }}>
            <Button
              size="small"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              sx={{ minWidth: 36 }}
            >
              Prev
            </Button>
            <Typography sx={{ mx: 2 }}>
              Page {page + 1} of {Math.ceil(filtered.length / rowsPerPage)}
            </Typography>
            <Button
              size="small"
              onClick={() => setPage(p => Math.min(Math.ceil(filtered.length / rowsPerPage) - 1, p + 1))}
              disabled={page >= Math.ceil(filtered.length / rowsPerPage) - 1}
              sx={{ minWidth: 36 }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog className="expense-dialog" open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 3, minWidth: 350 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#fb8c00', letterSpacing: 0.5 }}>{editId ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              name="expense_date"
              value={form.expense_date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Amount"
              type="number"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Autocomplete
              freeSolo
              options={defaultCategories}
              value={form.category}
              onChange={(_, newValue) => setForm(f => ({ ...f, category: newValue || '' }))}
              inputValue={form.category}
              onInputChange={(_, newInputValue) => setForm(f => ({ ...f, category: newInputValue }))}
              renderInput={(params) => (
                <TextField {...params} label="Category" name="category" fullWidth size="small" />
              )}
            />
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            {error && <Typography color="error" variant="caption">{error}</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="warning" disabled={loading} sx={{ fontWeight: 600 }}>{editId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
